import type { DefaultBodyType, HttpResponseResolver, PathParams, StrictRequest } from "msw";

import { waitForMockLatency } from "@/mocks/latency";
import type { PersonaId } from "@/mocks/personas";
import { unauthenticatedResponse } from "@/mocks/responses";
import { getPersonaIdFromRequest } from "@/mocks/session";
import { getPersonaState, type PersonaState } from "@/mocks/store";

type ResolverArgs<Params extends PathParams, RequestBody extends DefaultBodyType> = {
  params: Params;
  request: StrictRequest<RequestBody>;
};

type SessionResolverArgs<
  Params extends PathParams,
  RequestBody extends DefaultBodyType,
> = ResolverArgs<Params, RequestBody> & {
  personaId: PersonaId;
  state: PersonaState;
};

export const withLatency = <
  Params extends PathParams<keyof Params> = PathParams,
  RequestBody extends DefaultBodyType = DefaultBodyType,
>(
  resolver: (args: ResolverArgs<Params, RequestBody>) => Response | Promise<Response>,
): HttpResponseResolver<Params, RequestBody> => {
  return async ({ params, request }) => {
    await waitForMockLatency();

    return resolver({ params, request });
  };
};

export const withSession = <
  Params extends PathParams<keyof Params> = PathParams,
  RequestBody extends DefaultBodyType = DefaultBodyType,
>(
  resolver: (args: SessionResolverArgs<Params, RequestBody>) => Response | Promise<Response>,
): HttpResponseResolver<Params, RequestBody> => {
  return withLatency<Params, RequestBody>(({ params, request }) => {
    const personaId = getPersonaIdFromRequest(request);

    if (!personaId) {
      return unauthenticatedResponse();
    }

    return resolver({ params, request, personaId, state: getPersonaState(personaId) });
  });
};
